import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actions as globalToastActions } from 'components/GlobalToast/slice';

import { toI18n } from 'util/i18n';

import { getRequestsFeed } from '../api';
import { RequestGroup } from '../types';

type UseRequestsFeedParams = {
  tab: 'my' | 'team';
  locationId?: string;
  shouldFetch?: boolean;
};

export type UseRequestsFeedReturn = {
  requests: RequestGroup[];
  loading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  sentinelRef: React.RefObject<HTMLDivElement>;
  fetchInitialRequests: () => Promise<void>;
};

export const useRequestsFeed = ({
  tab,
  locationId,
  shouldFetch = false,
}: UseRequestsFeedParams): UseRequestsFeedReturn => {
  const [requests, setRequests] = useState<RequestGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  const fetchInitialRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequestsFeed({
        tab,
        locationId,
      });
      setRequests(res.requests);
      setCursor(res.cursor || null);
      setHasMore(!!res.cursor);
    } catch (error) {
      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n('fe_design_base.errors.general'),
          toastProps: { variant: 'error', autoHide: true, hideCloseIcon: true },
          initiatedFrom: 'requests_feed',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [tab, locationId, dispatch]);

  const loadMore = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;

    setIsFetchingMore(true);
    try {
      const res = await getRequestsFeed({
        tab,
        locationId,
        cursor: cursor || undefined,
      });
      setRequests(prev => [...prev, ...res.requests]);
      setCursor(res.cursor || null);
      setHasMore(!!res.cursor);
    } catch (error) {
      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n('fe_design_base.errors.general'),
          toastProps: { variant: 'error', autoHide: true, hideCloseIcon: true },
          initiatedFrom: 'requests_feed',
        })
      );
    } finally {
      setIsFetchingMore(false);
    }
  }, [isFetchingMore, hasMore, tab, locationId, cursor, dispatch]);

  // Fetch initial requests when shouldFetch is true
  useEffect(() => {
    if (shouldFetch) {
      fetchInitialRequests();
    }
  }, [shouldFetch, fetchInitialRequests]);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    if (loading || !sentinelRef.current) return;

    const options = {
      root: null,
      rootMargin: '250px',
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isFetchingMore) {
        loadMore();
      }
    }, options);

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, isFetchingMore, loadMore]);

  return {
    requests,
    loading,
    isFetchingMore,
    hasMore,
    sentinelRef,
    fetchInitialRequests,
  };
};
